/**
 * Floating Label Component
 * Creates an animated label effect where the label moves above the input field when focused or filled
 */
class FloatingLabel {
  constructor() {
    this.init();
  }

  init() {
    // Find all input fields and textareas that have a label
    this.setupFloatingLabels();
    
    // Listen for dynamic content changes
    this.observeDOMChanges();

    if (window.location.href.includes('pages/email-sign-up')) {
      this.settimeforemail();
    }
  }

  setupFloatingLabels() {
    // Get all inputs and textareas that are preceded by a label
    const fields = document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea');
   // console.log(`[FloatingLabel] Found ${fields.length} total fields to process`);
    
    fields.forEach(field => {
      const label = this.findLabelForField(field);
      
      // Remove placeholder from all fields
      if (field.hasAttribute('placeholder')) {
     //   console.log(`[FloatingLabel] Removing placeholder from field:`, field);
        field.removeAttribute('placeholder');
      }
      
      if (label && !label.classList.contains('floating-label')) {
      //  console.log(`[FloatingLabel] Setting up field with label:`, field, label);
        this.setupField(field, label);
      } else if (!label) {
        console.log(`[FloatingLabel] No label found for field:`, field);
      }
    });
  }

  settimeforemail() {
    // Handle dynamically loaded forms on email-sign-up page (e.g., Klaviyo embeds)
    // Try multiple times with increasing delays to catch forms that load asynchronously
    let attempts = 0;
    const maxAttempts = 20; // Try for up to ~2 seconds
    
    const checkAndSetup = () => {
      const fields = document.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea');
      
      // Check if we found any fields that haven't been processed yet
      const unprocessedFields = Array.from(fields).filter(field => {
        const label = this.findLabelForField(field);
        return label && !label.classList.contains('floating-label');
      });
      
      if (unprocessedFields.length > 0) {
      //  console.log(`[FloatingLabel] Found ${unprocessedFields.length} unprocessed fields on email-sign-up page, setting up...`);
        this.setupFloatingLabels();
      }
      
      // Continue checking if we haven't reached max attempts and haven't found all fields
      attempts++;
      if (attempts < maxAttempts) {
        requestAnimationFrame(() => {
          setTimeout(checkAndSetup, 100); // Check every 100ms
        });
      }
    };
    
    // Start checking
    checkAndSetup();
  }
  

  findLabelForField(field) {
    // Try to find label by 'for' attribute
    if (field.id) {
      const label = document.querySelector(`label[for="${field.id}"]`);
      if (label) return label;
    }
    
    // Try to find label as previous sibling
    const previousElement = field.previousElementSibling;
    if (previousElement && previousElement.tagName === 'LABEL') {
      return previousElement;
    }
    
    // Try to find label as parent's previous child
    // Added Klaviyo form selectors for embedded forms
    const parent = field.closest('.field, .contact-form__form-row, .blog-post-comments__form, .email-signup__input-group, .klaviyo-form-row, .form-row, .needsclick');
    if (parent) {
      const label = parent.querySelector('label');
      if (label) return label;
    }
    
    return null;
  }

  setupField(field, label) {
    const wrapper = field.parentElement;
    
    // Remove Klaviyo's screen-reader-only class if present
    if (label.classList.contains('klaviyo-sr-only')) {
     // console.log(`[FloatingLabel] Removing 'klaviyo-sr-only' class from label:`, label);
      label.classList.remove('klaviyo-sr-only');
    }
    
    // Remove visually-hidden class from label
    if (label.classList.contains('visually-hidden')) {
    //  console.log(`[FloatingLabel] Removing 'visually-hidden' class from label:`, label);
      label.classList.remove('visually-hidden');
    }
    
    // Add floating label class to label
    label.classList.add('floating-label');
    // console.log(`[FloatingLabel] Added 'floating-label' class to:`, label);
    
    // Add wrapper class for positioning
    if (!wrapper.classList.contains('floating-label-wrapper')) {
      wrapper.classList.add('floating-label-wrapper');
    }
    
    // Check if field has a value on load
    this.updateLabelState(field, label);
    
    // Add event listeners
    field.addEventListener('focus', () => this.onFieldFocus(field, label));
    field.addEventListener('blur', () => this.onFieldBlur(field, label));
    field.addEventListener('input', () => this.updateLabelState(field, label));
    
    // Handle autofill
    setTimeout(() => this.updateLabelState(field, label), 100);
  }

  onFieldFocus(field, label) {
    label.classList.add('floating-label--active');
  }

  onFieldBlur(field, label) {
    this.updateLabelState(field, label);
  }

  updateLabelState(field, label) {
    const hasValue = field.value.trim() !== '';
    const isFocused = document.activeElement === field;
    
    if (hasValue || isFocused) {
      label.classList.add('floating-label--active');
    } else {
      label.classList.remove('floating-label--active');
    }
  }

  observeDOMChanges() {
    // Observe for dynamically added forms (like in drawers, modals, or slow-loading embeds)
    let debounceTimer = null;
    
    const processNode = (node) => {
      if (node.nodeType !== 1) return; // Only process element nodes
      
      const fieldsToProcess = [];
      
      // Check if the node itself is a form field
      if (node.matches && node.matches('input[type="text"], input[type="email"], input[type="tel"], textarea')) {
        fieldsToProcess.push(node);
      }
      
      // Find all form fields within the node
      const childFields = node.querySelectorAll?.('input[type="text"], input[type="email"], input[type="tel"], textarea');
      if (childFields) {
        fieldsToProcess.push(...Array.from(childFields));
      }
      
      // Process all found fields
      if (fieldsToProcess.length > 0) {
       // console.log(`[FloatingLabel] MutationObserver detected ${fieldsToProcess.length} new field(s)`);
        
        fieldsToProcess.forEach(field => {
          // Remove placeholder immediately
          if (field.hasAttribute('placeholder')) {
           // console.log(`[FloatingLabel] Removing placeholder from dynamically added field:`, field);
            field.removeAttribute('placeholder');
          }
          
          const label = this.findLabelForField(field);
          if (label && !label.classList.contains('floating-label')) {
          //  console.log(`[FloatingLabel] Setting up dynamically added field:`, field, label);
            this.setupField(field, label);
          } else if (!label) {
            // Label might be added separately, retry after a short delay
          //  console.log(`[FloatingLabel] No label found yet for field, will retry:`, field);
            setTimeout(() => {
              const retryLabel = this.findLabelForField(field);
              if (retryLabel && !retryLabel.classList.contains('floating-label')) {
               // console.log(`[FloatingLabel] Found label on retry, setting up:`, field, retryLabel);
                this.setupField(field, retryLabel);
              }
            }, 200);
          }
        });
      }
    };
    
    const observer = new MutationObserver((mutations) => {
      // Debounce to avoid processing the same mutations multiple times
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        mutations.forEach((mutation) => {
          // Handle added nodes
          mutation.addedNodes.forEach((node) => {
            processNode(node);
          });
          
          // Handle attribute changes (e.g., placeholder being added dynamically)
          if (mutation.type === 'attributes' && mutation.attributeName === 'placeholder') {
            const field = mutation.target;
            if (field.hasAttribute('placeholder')) {
            //  console.log(`[FloatingLabel] Placeholder attribute added dynamically, removing:`, field);
              field.removeAttribute('placeholder');
            }
          }
        });
      }, 50); // Small delay to batch multiple mutations
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true, // Watch for attribute changes
      attributeFilter: ['placeholder'] // Only watch placeholder attribute
    });
    
    console.log('[FloatingLabel] MutationObserver initialized and watching for dynamic forms');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FloatingLabel();
  });
} else {
  new FloatingLabel();
}
